/**
 * COLOR RUSH — 350 fixed-speed balls (70 per color). Cursor picks a random color every 5s
 * (never the same twice in a row); collect matching balls (+1 score), die on wrong color.
 */

import { gameRandom } from "./game-rng";

export const COLOR_RUSH_BALLS_TOTAL = 350;
export const COLOR_RUSH_PER_COLOR = 70;
export const COLOR_RUSH_BALL_SPEED = 0.85;
export const COLOR_RUSH_CYCLE_MS = 5000;
export const COLOR_RUSH_RESPAWN_DELAY_MS = 2000;

let cycleMs = COLOR_RUSH_CYCLE_MS;
let respawnDelayMs = COLOR_RUSH_RESPAWN_DELAY_MS;

export function setColorRushTuning(opts?: { cycleMs?: number; respawnDelayMs?: number }): void {
    cycleMs =
        opts?.cycleMs != null && Number.isFinite(opts.cycleMs)
            ? Math.max(200, Math.floor(opts.cycleMs))
            : COLOR_RUSH_CYCLE_MS;
    respawnDelayMs =
        opts?.respawnDelayMs != null && Number.isFinite(opts.respawnDelayMs)
            ? Math.max(0, Math.floor(opts.respawnDelayMs))
            : COLOR_RUSH_RESPAWN_DELAY_MS;
}

export const COLOR_RUSH_PALETTE = [
    { id: 0, name: "red", hex: "#ff3333" },
    { id: 1, name: "green", hex: "#33dd33" },
    { id: 2, name: "blue", hex: "#3388ff" },
    { id: 3, name: "yellow", hex: "#ffdd22" },
    { id: 4, name: "orange", hex: "#ff8822" },
] as const;

let cursorColorIdx = 0;
let cycleAccumulatorMs = 0;
let colorFlashUntil = 0;
let respawnScheduledAt = 0;

function pickRandomCursorColor(exclude?: number) {
    const n = COLOR_RUSH_PALETTE.length;
    if (n <= 1) return 0;
    if (exclude == null || exclude < 0 || exclude >= n) {
        return Math.floor(gameRandom() * n);
    }
    // Exactly one RNG sample among n-1 options (never the excluded color).
    const r = Math.floor(gameRandom() * (n - 1));
    return r >= exclude ? r + 1 : r;
}

export function resetColorRushState() {
    cursorColorIdx = pickRandomCursorColor();
    cycleAccumulatorMs = 0;
    colorFlashUntil = 0;
    respawnScheduledAt = 0;
}

export function buildColorRushColorDeck(total = COLOR_RUSH_BALLS_TOTAL): number[] {
    const n = Math.max(COLOR_RUSH_PALETTE.length, Math.floor(Number(total) || COLOR_RUSH_BALLS_TOTAL));
    const perColor = Math.floor(n / COLOR_RUSH_PALETTE.length);
    let remainder = n - perColor * COLOR_RUSH_PALETTE.length;
    const deck: number[] = [];
    for (let c = 0; c < COLOR_RUSH_PALETTE.length; c++) {
        const count = perColor + (remainder > 0 ? 1 : 0);
        if (remainder > 0) remainder--;
        for (let i = 0; i < count; i++) deck.push(c);
    }
    for (let i = deck.length - 1; i > 0; i--) {
        const j = Math.floor(gameRandom() * (i + 1));
        const tmp = deck[i];
        deck[i] = deck[j];
        deck[j] = tmp;
    }
    return deck;
}

/** Returns true when the cursor color just changed (for flash / SFX). */
export function tickColorRushColorCycle(dtMs: number, now: number): boolean {
    cycleAccumulatorMs += dtMs;
    if (cycleAccumulatorMs < cycleMs) return false;
    cycleAccumulatorMs -= cycleMs;
    cursorColorIdx = pickRandomCursorColor(cursorColorIdx);
    colorFlashUntil = now + 420;
    return true;
}

export function getColorRushCursorColorIdx() {
    return cursorColorIdx;
}

export function getColorRushCursorHex() {
    return COLOR_RUSH_PALETTE[cursorColorIdx].hex;
}

export function getColorRushBallHex(colorIdx: number) {
    const row = COLOR_RUSH_PALETTE[colorIdx];
    return row ? row.hex : COLOR_RUSH_PALETTE[0].hex;
}

export function isColorRushCursorPulsing(now: number) {
    return now < colorFlashUntil;
}

export function getColorRushCursorPulseScale(now: number) {
    if (!isColorRushCursorPulsing(now)) return 1;
    const t = 1 - (colorFlashUntil - now) / 420;
    return 1 + Math.sin(t * Math.PI) * 0.42;
}

export function scheduleColorRushRespawn(now: number) {
    if (respawnScheduledAt > 0) return;
    respawnScheduledAt = now + respawnDelayMs;
}

/** Returns true once when the respawn delay has elapsed. */
export function tickColorRushRespawn(now: number) {
    if (respawnScheduledAt <= 0 || now < respawnScheduledAt) return false;
    respawnScheduledAt = 0;
    return true;
}

export function isColorRushAwaitingRespawn() {
    return respawnScheduledAt > 0;
}
